import { RefreshCw } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { useIsPureAdmin } from '@/react/hooks/useUser';

import { LoadingButton } from '@@/buttons';
import { confirm } from '@@/modals/confirm';
import { ModalType } from '@@/modals';
import { buildConfirmButton } from '@@/modals/utils';

import { useRescanWorkload } from './queries/useRescanWorkload';

/**
 * Asks the scanner to look at a workload again by deleting the reports it holds
 * for it. Findings recorded under an older policy - a wider severity list, say -
 * only change when the report is replaced.
 *
 * Renders nothing for anyone but an admin: deleting the reports goes through the
 * environment's Kubernetes API, where only an admin holds access to the
 * aquasecurity.github.io resources today, so a non-admin would only ever get a
 * 403 from the button.
 */
export function RescanWorkloadButton({
  environmentId,
  namespace,
  workload,
  onRescanned,
}: {
  environmentId: EnvironmentId;
  namespace: string;
  workload: string;
  onRescanned?: () => void;
}) {
  const isPureAdmin = useIsPureAdmin();
  const rescanMutation = useRescanWorkload(environmentId);

  if (!isPureAdmin) {
    return null;
  }

  return (
    <LoadingButton
      type="button"
      color="default"
      icon={RefreshCw}
      isLoading={rescanMutation.isLoading}
      loadingText="Requesting rescan"
      onClick={handleClick}
      data-cy="vulnerability-rescan-workload"
    >
      Rescan
    </LoadingButton>
  );

  async function handleClick() {
    const confirmed = await confirm({
      title: 'Rescan this workload?',
      modalType: ModalType.Warn,
      message: `The findings currently recorded for ${workload} are deleted and the scanner records them again. They stay unavailable until the new scan finishes, which can take a few minutes on a busy cluster.`,
      confirmButton: buildConfirmButton('Rescan'),
    });
    if (!confirmed) {
      return;
    }

    rescanMutation.mutate(
      { namespace, workload },
      {
        onSuccess: () => {
          notifySuccess('Success', `Rescan requested for ${workload}`);
          onRescanned?.();
        },
      }
    );
  }
}
